// Per-user portal preferences. Stored as a JSONB blob on auth.app_user.
// Always tolerate missing / unknown fields — the API may return {} for
// new users.

export type ThemeMode = 'light' | 'dark' | 'system';

export type PortalPreferences = {
  themeMode?: ThemeMode;
  /**
   * If set, the user's primary brand color overrides whichever
   * operator branding is in effect. Use to opt out of an operator's
   * theme entirely (e.g. ACES support staff who jump between
   * operators and want a stable look).
   */
  primaryHex?: string;
  accentHex?: string;
};

const VALID_MODES = new Set<ThemeMode>(['light', 'dark', 'system']);
// Shared 6-digit hex format across every portal preference writer (theme
// colours here, the SAS analyser's per-role palette in
// pages/sas-analyzer/colorPreferences.ts) — one definition, no drift.
export const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export function parsePreferences(input: unknown): PortalPreferences {
  if (!input || typeof input !== 'object') return {};
  const obj = input as Record<string, unknown>;
  const out: PortalPreferences = {};

  const mode = obj.themeMode;
  if (typeof mode === 'string' && VALID_MODES.has(mode as ThemeMode)) {
    out.themeMode = mode as ThemeMode;
  }

  const primary = obj.primaryHex;
  if (typeof primary === 'string' && HEX_RE.test(primary)) {
    out.primaryHex = primary;
  }

  const accent = obj.accentHex;
  if (typeof accent === 'string' && HEX_RE.test(accent)) {
    out.accentHex = accent;
  }

  return out;
}

/**
 * Read-merge-write helper shared by every `/me/preferences` writer.
 * `PUT /api/v1/me/preferences` REPLACES the whole stored JSON object
 * (MeEndpoints.cs) — it doesn't do a server-side merge — so any partial
 * save (theme mode here, the SAS analyser's per-role colour palette in
 * `pages/sas-analyzer/colorPreferences.ts`, and anything added later)
 * must merge its patch onto whatever's already stored rather than
 * clobbering the rest of the object. This was a real bug: saving the
 * Appearance page used to PUT a bare `{ themeMode, primaryHex, accentHex }`
 * object, silently wiping any other key (e.g. `sasAnalyzer.colors`) that
 * had been saved by another page.
 *
 * Shallow merge: `current` is spread first (guarded against `null` /
 * non-object — e.g. a brand-new user's `{}`), then every key in `patch`
 * overwrites it. Never mutates `current`. A caller that owns a nested
 * sub-object (e.g. `sasAnalyzer.colors`) merges that nesting itself before
 * calling this — see `mergeSasAnalyzerColors`.
 */
export function mergePreferencePatch(
  current: unknown,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const base: Record<string, unknown> =
    current && typeof current === 'object' ? { ...(current as Record<string, unknown>) } : {};
  return { ...base, ...patch };
}
